import {Repository} from "./Repository";
import {AxiosInstance} from "axios";
import {QueryParametersInterface} from "./interfaces/QueryParametersInterface";
import {MeetingInterface} from "../models/interfaces/meetings/MeetingInterface";
import {MeetingSaveInterface} from '../models/interfaces/meetings/MeetingSaveInterface';
import {Meeting} from "../models/meetings/Meeting";
import {ParticipantStatuses} from "@api/enums/Meetings/ParticipantStatuses";
import {Invite} from "../models/meetings/Invite";
import {DateTime} from "luxon";
import {Busyness} from "../models/meetings/Busyness";
import {WebinarInfo} from "../models/WebinarInfo";
import {AppError} from "../../common/AppError";
import { MScheduleSlotCreateInterface } from "@api/models/interfaces/meetings-schedule/MeetingsScheduleSlotInterface";
import { MScheduleSlot } from "@api/models/meetings-schedule/MeetingsScheduleSlot";
import { CreatePublicMeetingInterface } from "@api/models/interfaces/meetings/CreatePublicMeetingInterface";

export class MeetingRepository implements Repository {
    client: AxiosInstance;

    constructor(client: AxiosInstance) {
        this.client = client;
    }

    query(query: QueryParametersInterface = {}): Promise<Meeting[]> {
        return this.client.post('/meetings/query', query)
            .then(({data}) => {
                return data.map((item: MeetingInterface) => new Meeting(item))
            });
    }

    create(data: MeetingSaveInterface) {
        return this.client.post<Meeting>('/meetings', data)
            .then(({data}) => {
                return new Meeting(data);
            })
    }

    save(id: string, data: MeetingSaveInterface) {
        return this.client.put<Meeting>(`/meetings/${id}`, data)
            .then(({data}) => {
                return new Meeting(data);
            })
    }

    webinar(meetingId: string) {
        return this.client.get(`/meetings/${meetingId}/join`)
            .then((response) => {
                return new WebinarInfo(response.data);
            })
            .catch(e => {
                if (e.response && e.response.status >= 500) {
                    throw new AppError('BackendUnexpectedError', e.message, e)
                } else {
                    throw e;
                }
            })
    }

    schemaWebinar(schemaId: string, objectId: string) {
        return this.client.get(`/meetings/${schemaId}/${objectId}/join`)
            .then((response) => {
                return new WebinarInfo(response.data);
            })
            .catch(e => {
                if (e.response && e.response.status >= 500) {
                    throw new AppError('BackendUnexpectedError', e.message, e)
                } else {
                    throw e;
                }
            })
    }

    find(id: string) {
        return this.client.get(`/meetings/${id}`)
            .then(({data}) => {
                return new Meeting(data || {});
            })
    }

    changeStatus(participantId: string, status: ParticipantStatuses) {
        return this.client.put<Meeting>(`/meetings/invite/${participantId}/status`, `"${status}"`)
            .then(({data}) => {
                return new Meeting(data);
            })
    }

    invites(eventId: string, take = 20, skip = 0, count = false, orderByDesc = false, statuses: string[] = [], type = 'received') {
        return this.client.get<Invite[]>(`/meetings/invites`, {
            params: {
                eventId,
                take,
                skip,
                count,
                orderByDesc,
                statuses,
                type
            },
            paramsSerializer: (params: Record<string, any>) => {
                const urlParams = new URLSearchParams();
                for(const paramName in params) {
                    if (Object.prototype.hasOwnProperty.call(params, paramName)) {
                        if (Array.isArray(params[paramName])) {
                            params[paramName].forEach((item: any) => {
                                urlParams.append(paramName, item);
                            })
                        }
                        else {
                            urlParams.append(paramName, params[paramName]);
                        }
                    }
                }
                return urlParams.toString();
            }
        })
            .then(({data, headers}) => {
                return {invites: data.map(item => new Invite(item)), count: parseInt(headers['x-appercode-totalitems'] || 0)};
            })
    }

    invitesCount(eventId: string) {
        return this.client.get<number>(`/meetings/invites`, {
            params: {
                eventId,
                take: 0,
                skip: 0,
                count: true
            }
        })
            .then(({headers}) => {
                return parseInt(headers['x-appercode-totalitems'] || 0);
            });
    }

    busyness(date: DateTime, userIds: number[], schemaId: string) {
        return this.client.post<Busyness[]>(`/meetings/busyness/query`, userIds, {
            params: {
                date: date.toISO(),
                schemaId
            }
        })
            .then(({data}) => {
                return data.map(item => new Busyness(item));
            })
    }

    delete(id: string) {
        return this.client.delete(`/meetings/${id}`);
    }

    createSlot(updateModel: MScheduleSlotCreateInterface) {
        return this.client.post('/meetings/slot', updateModel).then(({ data }) => new MScheduleSlot(data));
    }

    deleteSlot(slotId: string) {
        return this.client.put(`/meetings/slot/${slotId}`, { isDeleted: true });
    }

    public(parameters: CreatePublicMeetingInterface) {
        return this.client.post('/meetings/public', parameters);
    }

    startRecording(id: string)
    {
        return this.client.post(`/meetings/${id}/start-recording`);
    }

    stopRecording(id: string)
    {
        return this.client.post(`/meetings/${id}/stop-recording`);
    }
}
